import { SkeletonBase } from "@components/common/skeletons";
import { cn } from "@/lib/utils/cn";

/**
 * Skeleton loader variants for dashboard widgets
 */
export type SkeletonVariant = "stats" | "card" | "list" | "full";

/**
 * Props for SkeletonWidget component
 */
export interface SkeletonWidgetProps {
  /** Number of skeleton items to show */
  count?: number;
  /** Skeleton variant */
  variant?: SkeletonVariant;
  /** CSS classes */
  className?: string;
  /** Show title skeleton */
  showTitle?: boolean;
  /** Title width */
  titleWidth?: string;
  /** Number of content lines */
  lines?: number;
}

/**
 * Stats skeleton for metric cards
 */
function StatsSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("stats shadow-md border border-base-300 animate-pulse", className)}>
      <div className="stat">
        <div className="flex justify-between items-start mb-2">
          <SkeletonBase variant="line" className="h-4 w-32" />
          <SkeletonBase variant="circle" className="h-6 w-6" />
        </div>
        <SkeletonBase variant="line" className="h-8 w-20 mb-2" />
        <SkeletonBase variant="line" className="h-3 w-24" />
      </div>
    </div>
  );
}

/**
 * Card skeleton for list/grid cards
 */
function CardSkeleton({ showTitle = true, titleWidth = "w-3/4", lines = 3 }: SkeletonWidgetProps) {
  return (
    <div className="card bg-base-200 shadow-sm border border-base-300/60 p-4">
      {showTitle && <SkeletonBase variant="line" className={cn("h-5 mb-3", titleWidth)} />}
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, index) => (
          <SkeletonBase
            key={index}
            variant="line"
            className={index === lines - 1 ? "w-1/2" : "w-full"}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * List skeleton for activity feeds
 */
function ListSkeleton({ count = 5, lines = 2 }: { count?: number; lines?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex items-start gap-3">
          <SkeletonBase variant="circle" className="h-10 w-10 flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <SkeletonBase variant="line" className="h-4 w-full" />
            <SkeletonBase variant="line" className={cn("h-3", index === 0 ? "w-1/3" : "w-1/2")} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Dashboard skeleton grid loader
 * Mimics the layout of the HomeDashboard widgets
 */
export function DashboardSkeletonGrid({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-6", className)}>
      {/* Top row - Personal metrics (3 stats) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatsSkeleton />
        <StatsSkeleton />
        <StatsSkeleton />
      </div>

      {/* Middle row - Quick actions + Recent activity (2 cards) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CardSkeleton titleWidth="w-1/3" lines={4} />
        <ListSkeleton count={4} lines={2} />
      </div>

      {/* Bottom row - Pending approvals + Team presence (2 cards) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CardSkeleton titleWidth="w-1/2" lines={3} />
        <ListSkeleton count={5} lines={1} />
      </div>
    </div>
  );
}

/**
 * Generic skeleton widget loader
 */
export function SkeletonWidget({
  count = 1,
  variant = "card",
  className,
  showTitle = true,
  titleWidth = "w-3/4",
  lines = 3,
}: SkeletonWidgetProps) {
  if (variant === "stats") {
    return (
      <div className={cn("grid grid-cols-1", className)}>
        {Array.from({ length: count }).map((_, index) => (
          <StatsSkeleton key={index} className={index > 0 ? "mt-4" : undefined} />
        ))}
      </div>
    );
  }

  if (variant === "list") {
    return (
      <div className={className}>
        <ListSkeleton count={count} lines={lines} />
      </div>
    );
  }

  if (variant === "full") {
    return <DashboardSkeletonGrid className={className} />;
  }

  // Default: card variant
  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", className)}>
      {Array.from({ length: count }).map((_, index) => (
        <CardSkeleton key={index} showTitle={showTitle} titleWidth={titleWidth} lines={lines} />
      ))}
    </div>
  );
}
